import type { Building } from "building-types/building/buildingTypes";
import type {
  FootingFoundationKAM,
  FootingFoundationKAMOptions,
  FootingFoundationKeyAttributes,
  FootingFoundationKeyMeasures,
} from "building-types/dataSequences/builtin/FootingFoundationKAM";
import type {
  SlabFoundationKAM,
  SlabFoundationKAMOptions,
  SlabFoundationKeyAttributes,
  SlabFoundationKeyMeasures,
} from "building-types/dataSequences/builtin/SlabFoundationKAM";
import type { BuildingEntityFilter } from "building-types/dataSequences/filterableBuildingEntityTypes";
import type { KeyAttributesAndMeasuresDisplayOptions } from "building-types/dataSequences/measures";
import type {
  StandardFoundationKeyMeasure,
  StandardFoundationKeyMeasureSet,
} from "building-types/foundations/standardFoundationKeyMeasureTypes";
import {
  keyAttributeNew,
  keyMeasureNew,
  keyAttributeOptionsNew,
  keyMeasureOptionsNew,
} from "doc/utils/dataSequenceTools";
import { getFootingFoundationsKeyMeasures, getSlabFoundationsKeyMeasures } from "procure/foundationKeyMeasures";

function formatStringValue(value: string, undefinedVal = "") {
  return value !== undefined && value !== null ? value : undefinedVal;
}

export function makeSlabFoundationKAM(foundation: StandardFoundationKeyMeasure): SlabFoundationKAM {
  return {
    type: "SLAB",
    attributes: {
      type: keyAttributeNew(formatStringValue(foundation.name, "NA")),
      category: keyAttributeNew(foundation.category),
      order: keyAttributeNew(foundation.order.toString()),
      entityName: keyAttributeNew(foundation.category + foundation.name),
    },
    measures: {
      area: keyMeasureNew(foundation.area ?? 0, "SQ FT"),
      volume: keyMeasureNew(foundation.volume ?? 0, "CU FT"),
      count: keyMeasureNew(1),
    },
    handles: foundation.handles,
  };
}
export const EMPTY_SLAB_FOUNDATION_KAM: SlabFoundationKAM = {
  type: "SLAB",
  attributes: {
    type: keyAttributeNew(""),
    category: keyAttributeNew(""),
    order: keyAttributeNew(""),
    entityName: keyAttributeNew(""),
  },
  measures: {
    area: keyMeasureNew(0, "SQ FT"),
    volume: keyMeasureNew(0, "CU FT"),
    count: keyMeasureNew(0),
  },
  handles: [],
};
export function makeDefaultSlabFoundationKAMOptions(): SlabFoundationKAMOptions {
  return {
    attributes: {
      entityName: keyAttributeOptionsNew({ definesEntity: false }),
    },
    measures: {
      area: keyMeasureOptionsNew({ unit: "SQ FT" }),
      volume: keyMeasureOptionsNew({ unit: "CU FT" }),
    },
  };
}
export const defaultSlabFoundationKAMSort = (a: SlabFoundationKAM, b: SlabFoundationKAM) => {
  return a.attributes.order.value.localeCompare(b.attributes.order.value, undefined, { numeric: true });
};

export function makeFootingFoundationKAM(foundation: StandardFoundationKeyMeasure): FootingFoundationKAM {
  return {
    type: "FOOTING",
    attributes: {
      type: keyAttributeNew(formatStringValue(foundation.name, "NA")),
      category: keyAttributeNew(foundation.category),
      order: keyAttributeNew(foundation.order.toString()),
      entityName: keyAttributeNew(foundation.category + foundation.name),
    },
    measures: {
      length: keyMeasureNew(foundation.length ?? 0, "FEET"),
      volume: keyMeasureNew(foundation.volume ?? 0, "CU FT"),
      count: keyMeasureNew(1),
    },
    handles: foundation.handles,
  };
}
export const EMPTY_FOOTING_FOUNDATION_KAM: FootingFoundationKAM = {
  type: "FOOTING",
  attributes: {
    type: keyAttributeNew(""),
    category: keyAttributeNew(""),
    order: keyAttributeNew(""),
    entityName: keyAttributeNew(""),
  },
  measures: {
    length: keyMeasureNew(0, "INCHES"),
    volume: keyMeasureNew(0, "CU IN"),
    count: keyMeasureNew(0),
  },
  handles: [],
};
export function makeDefaultFootingFoundationKAMOptions(): FootingFoundationKAMOptions {
  return {
    attributes: {
      entityName: keyAttributeOptionsNew({ definesEntity: false }),
    },
    measures: {
      length: keyMeasureOptionsNew({ unit: "FEET" }),
      volume: keyMeasureOptionsNew({ unit: "CU FT" }),
    },
  };
}
export const defaultFootingFoundationKAMSort = (a: FootingFoundationKAM, b: FootingFoundationKAM) => {
  return a.attributes.order.value.localeCompare(b.attributes.order.value, undefined, { numeric: true });
};

/** @deprecated - use the Building Query API instead */
export function buildingFoundationKAMs(
  building: Building,
  filter?: BuildingEntityFilter,
): { footings: FootingFoundationKAM[] } {
  const foundationSubSystems = Object.values(building.foundations);

  const footingKeyMeasures: StandardFoundationKeyMeasureSet = getFootingFoundationsKeyMeasures(
    building,
    foundationSubSystems,
  );

  const footings: FootingFoundationKAM[] = [];

  for (const footingId in footingKeyMeasures) {
    const measure = footingKeyMeasures[footingId];
    if (filter && !filter(measure)) {
      continue;
    }
    footings.push(makeFootingFoundationKAM(measure));
  }

  return { footings };
}

/** @deprecated - use the Building Query API instead */
export function buildingFoundationSuperSystemKAMs(
  building: Building,
  filter?: BuildingEntityFilter,
): { slabs: SlabFoundationKAM[] } {
  const foundationSuperSystems = Object.values(building.foundationSuperSystems);

  const slabKeyMeasures: StandardFoundationKeyMeasureSet = getSlabFoundationsKeyMeasures(
    building,
    foundationSuperSystems,
  );

  const slabs: SlabFoundationKAM[] = [];
  for (const slabId in slabKeyMeasures) {
    const measure = slabKeyMeasures[slabId];
    if (filter && !filter(measure)) {
      continue;
    }
    slabs.push(makeSlabFoundationKAM(measure));
  }

  return { slabs };
}

export const SLAB_DISPLAY_OPTIONS: KeyAttributesAndMeasuresDisplayOptions<
  SlabFoundationKeyAttributes,
  SlabFoundationKeyMeasures
> = {
  attributes: {
    entityName: {
      hidden: true,
    },
    category: {
      hidden: true,
    },

    order: {
      hidden: true,
    },
  },
};

// Attributes used in procure entityHash
export const DEFINES_SLAB: (keyof SlabFoundationKeyAttributes)[] = ["category", "type"];

export const FOOTING_DISPLAY_OPTIONS: KeyAttributesAndMeasuresDisplayOptions<
  FootingFoundationKeyAttributes,
  FootingFoundationKeyMeasures
> = {
  attributes: {
    entityName: {
      hidden: true,
    },
    category: {
      hidden: true,
    },

    order: {
      hidden: true,
    },
  },
};

// Attributes used in procure entityHash
export const DEFINES_FOOTING: (keyof FootingFoundationKeyAttributes)[] = ["category", "type"];
